import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Option, PermissionLevel, SessionService, UiArguments } from "@recore-solutions/nstar-lib";
import { of } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";
import { CodeTableService } from "src/app/listing/code-table.service";
import { ListingService } from "./listing.service";
import { LoonFnFactoriesService } from "./loon-fn-factories.service";

@Injectable({ providedIn: "root" })
export class MemberFnFactoriesService extends LoonFnFactoriesService {
	constructor(
		protected listingService: ListingService,
		protected sessionService: SessionService,
		protected codeTableService?: CodeTableService
	) {
		super(listingService, codeTableService);
	}

	queryTransformFn = (uiArguments: UiArguments) => (fullTextSearch: string | number) => {
		if (!!fullTextSearch && typeof fullTextSearch === "number") {
			return new HttpParams({
				fromObject: {
					[uiArguments.uiComponent.databaseTable + "." + uiArguments.uiComponent.databaseColumn]: (
						fullTextSearch as number
					).toString(),
				},
			});
		} else if (!!fullTextSearch) {
			return new HttpParams({
				fromObject: {
					fullTextSearch,
					"OfficeMember.Expiration_UTCDTTM": "FUTURE",
				},
			});
		}
		return null;
	};

	searchFn = (uiArguments: UiArguments) => {
		const { listingOrganizationSK } = uiArguments.listingSummary;
		const permissionLevel$ = this.sessionService.permissionLevel$.pipe(
			map((permissionLevels: PermissionLevel[]) =>
				this.mapPermissionLevelsToPermissionLevel(permissionLevels, listingOrganizationSK)
			)
		);

		return (queryObj: HttpParams) => {
			if (!queryObj || queryObj.keys().length === 0) {
				return of([]);
			}

			if (["Co-Listing Agent"].includes(this.getParentFromUiArguments(uiArguments).longName)) {
				let coListingHttpParams = new HttpParams({
					fromObject: {
						accessOverride: "Y",
						"OfficeMember.Expiration_UTCDTTM": "FUTURE",
					},
				});

				if (queryObj.has("fullTextSearch")) {
					coListingHttpParams = coListingHttpParams.set(
						"fullTextSearch",
						queryObj.get("fullTextSearch")
					);
				}

				if (queryObj.has("Member.Member_SK")) {
					coListingHttpParams = coListingHttpParams.set(
						"Member.Member_SK",
						queryObj.get("Member.Member_SK")
					);
				}
				return this.getSearchMembers(coListingHttpParams);
			}

			const { listingStatusCD } = this.getListingSummaryExtract(uiArguments.formGroup);
			// sale/co-sales agent when status is pending, sold or rented
			const sellingFields = [
				"Selling Agent",
				"Co-Selling Agent",
				"Renting Agent",
				"Co-Renting Agent",
			];
			if (
				["PEND", "CLOSD", "RENTD"].includes(listingStatusCD) &&
				queryObj?.has("fullTextSearch") &&
				sellingFields.includes(this.getParentFromUiArguments(uiArguments).longName)
			) {
				const httpParams = new HttpParams({
					fromObject: {
						accessOverride: "Y",
						"OfficeMember.Expiration_UTCDTTM": "FUTURE",
						fullTextSearch: queryObj.get("fullTextSearch"),
					},
				});
				return this.getSearchMembers(httpParams);
			}

			return permissionLevel$.pipe(
				take(1),
				switchMap(this.switchMapBasedOnPermissionLevel(queryObj))
			);
		};
	};

	switchMapBasedOnPermissionLevel =
		(queryObj: HttpParams) => (permissionLevels: PermissionLevel[]) => {
			const permissionLevel = permissionLevels.find((pl) => pl.primaryOfficeIND === "Y");
			switch (permissionLevel?.accessName) {
				// Office Broker will get Office AND Firm limits.
				case "Office Broker":
					if (queryObj?.has("fullTextSearch")) {
						queryObj = queryObj.set(
							"Office.Office_SK",
							permissionLevels.map((pl) => pl.officeSK).join(",")
						);
					}
				// Head Broker will get Firm limits.
				case "Head Broker":
					if (queryObj?.has("fullTextSearch")) {
						queryObj = queryObj.set("Office.Firm_SK", permissionLevel.firmSK);
					}
				// Office Broker and Head Broker will fall through to here, along with these other levels.
				case "Association Staff":
				case "Organization Staff":
				case "Super User":
					if (!queryObj || queryObj.keys().length === 0) {
						return of([]);
					}
					if (queryObj?.has("fullTextSearch")) {
						return this.getSearchMembers(queryObj);
					} else {
						return this.getMembers(queryObj);
					}
				default:
					if (queryObj?.has("fullTextSearch")) {
						return this.getCoTeamMembers(permissionLevel, queryObj);
					} else {
						return this.getMembers(queryObj);
					}
			}
		};

	getCoTeamMembers = (permissionLevel: PermissionLevel, queryObj?: HttpParams) => {
		const mainHttpParams = this.mapPermissionLevelToTeamHttpParams(permissionLevel, queryObj);
		return this.getTeamMembers(mainHttpParams).pipe(
			switchMap((teamMembers) => {
				const memberSK =
					teamMembers.length > 0
						? teamMembers.map((x) => x.memberSK).join(",")
						: mainHttpParams.get("CoTeamMember.Member_SK");
				return this.getSearchMembers(new HttpParams().set("Member.Member_SK", memberSK));
			})
		);
	};

	mapPermissionLevelToTeamHttpParams = (
		permissionLevel: PermissionLevel,
		httpParams?: HttpParams
	): HttpParams => {
		httpParams = httpParams || new HttpParams();
		if (httpParams.has("fullTextSearch")) {
			httpParams = httpParams.set("memberSearch", httpParams.get("fullTextSearch"));
			httpParams = httpParams.delete("fullTextSearch");
		}
		if (!!permissionLevel?.memberSK) {
			httpParams = httpParams.set("CoTeamMember.Member_SK", permissionLevel.memberSK);
		}
		httpParams = httpParams.set("Team.Expiration_UTCDTTM", "FUTURE");
		httpParams = httpParams.set("TeamMember.Expiration_UTCDTTM", "FUTURE");
		httpParams = httpParams.set(
			"vwCurrentMemberSubscription.OfficeMemberExpiration_UTCDTTM",
			"FUTURE"
		);
		httpParams = httpParams.set("vwCurrentMemberSubscription.ServiceOfferingName", "Add/Edit");

		return httpParams;
	};

	resultTransformFn = () => (results: any[]) => {
		return results.map((r) => ({
			...r,
			key: r.memberSK,
			value: `${r.memberFullName} (${r.memberReference})`,
			sortOrder: 0,
		}));
	};

	resultFindFn = () => (value: any) => (option: Option & any) => {
		return value === option?.key;
	};
}
